import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const HowItWorks = () => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const sectionRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (sectionRef.current) {
                const rect = sectionRef.current.getBoundingClientRect();
                setMousePosition({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                });
            }
        };

        const section = sectionRef.current;
        if (section) {
            section.addEventListener('mousemove', handleMouseMove);
            return () => section.removeEventListener('mousemove', handleMouseMove);
        }
    }, []);

    const steps = [
        {
            icon: "🗓️",
            title: "Join & Book Intro",
            description: "Create your account in seconds and book a free 15-minute introduction with our academic director."
        },
        {
            icon: "🧪",
            title: "Select Your Subjects",
            description: "Choose from our wide range of British curriculum subjects (GCSE, A-Level) tailored to your goals."
        },
        {
            icon: "🎓",
            title: "Book & Start Learning",
            description: "Bulk book sessions for a discount or pay as you go. Start mastering your subjects immediately."
        }
    ];

    return (
        <section id="how-it-works" className="section how-it-works-interactive" ref={sectionRef}>
            <div className="cursor-glow" style={{
                left: `${mousePosition.x}px`,
                top: `${mousePosition.y}px`,
                opacity: hoveredIndex !== null ? 0.3 : 0.1
            }}></div>

            <div className="section-header">
                <h2 className="section-title">
                    How It <span className="highlight-yellow">Works</span>
                </h2>
                <p className="section-subtitle">
                    Get started in minutes and see results in weeks. Our proven 3-step process has helped students achieve their goals.
                </p>
            </div>

            <div className="how-it-works-steps">
                {steps.map((step, index) => (
                    <motion.div
                        key={index}
                        className="how-it-works-step"
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        whileHover={{
                            scale: 1.05,
                            y: -10,
                            transition: { duration: 0.3 }
                        }}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                    >
                        <motion.div
                            className="step-number"
                            whileHover={{ rotate: 360, scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                        >
                            {index + 1}
                        </motion.div>
                        <motion.div
                            className="step-icon"
                            animate={hoveredIndex === index ? {
                                scale: 1.2,
                                rotate: [0, -10, 10, -10, 0],
                            } : { scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            {step.icon}
                        </motion.div>
                        <h3 className="step-title">{step.title}</h3>
                        <p className="step-description">{step.description}</p>
                        {index < steps.length - 1 && (
                            <motion.div
                                className="step-connector"
                                animate={hoveredIndex === index ? {
                                    scaleX: 1.2,
                                    backgroundColor: 'var(--color-yellow-cta)'
                                } : {}}
                            />
                        )}
                    </motion.div>
                ))}
            </div>


        </section>
    );
};

export default HowItWorks;
